import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ArrowBack, Person, CalendarToday, Article } from "@mui/icons-material";
import noticias from "../data/noticias.js";

export const NoticiasDetalhes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const noticia = noticias.find((n) => n.id === parseInt(id));

  if (!noticia) {
    return (
      <Box
        sx={{
          backgroundColor: "#f8f9fa",
          minHeight: "100vh",
          pt: { xs: "80px", md: "100px" },
          pb: { xs: 4, md: 6 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h4" color="error" gutterBottom>
              Notícia não encontrada
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBack />}
              onClick={() => navigate("/home")}
              sx={{
                backgroundColor: "primary.main",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              }}
            >
              Voltar a página principal
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  const formatDate = (dateString) => {
    // Se a string contém "a" (indicando um período), retorna como string
    if (
      typeof dateString === "string" &&
      (dateString.includes(" a ") || dateString.includes("-"))
    ) {
      return dateString;
    }

    // Caso contrário, tenta formatar como data
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Se não for uma data válida, retorna como string
      }
      return date.toLocaleDateString("pt-BR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return dateString; // Em caso de erro, retorna a string original
    }
  };

  const formatContent = (content) => {
    return content.split("\n").map(
      (paragraph, index) =>
        paragraph.trim() && (
          <Typography
            key={index}
            variant="body1"
            paragraph
            sx={{
              lineHeight: 1.8,
              fontSize: { xs: "1rem", md: "1.1rem" },
              color: "#333",
              textAlign: "justify",
            }}
          >
            {paragraph.trim()}
          </Typography>
        ),
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        pt: { xs: "80px", md: "100px" },
        pb: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="md">
        {/* Botão Voltar */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/home")}
          sx={{
            color: "primary.main",
            fontWeight: "bold",
            textTransform: "none",
            mb: 3,
            "&:hover": {
              backgroundColor: "rgba(139, 38, 53, 0.04)",
            },
          }}
        >
          Voltar a página principal
        </Button>

        {/* Conteúdo Principal */}
        <Paper
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          }}
        >
          {/* Imagem de Destaque */}
          <Box
            component="img"
            src={noticia.image}
            alt={noticia.title}
            sx={{
              width: "100%",
              height: { xs: 250, md: 350 },
              objectFit: "cover",
              backgroundColor: "#f0f0f0",
            }}
            onError={(e) => {
              e.target.src = `data:image/svg+xml;base64,${btoa(`
                <svg width="800" height="400" xmlns="http://www.w3.org/2000/svg">
                  <rect width="100%" height="100%" fill="${theme.palette.primary.main}"/>
                  <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="24" 
                        fill="white" text-anchor="middle" dy="0.3em">
                    Imagem não encontrada
                  </text>
                </svg>
              `)}`;
            }}
          />

          {/* Conteúdo */}
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            {/* Meta informações */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 2, md: 3 },
                mb: 3,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Article sx={{ fontSize: 18, color: "primary.main" }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: "500" }}
                >
                  Notícia
                </Typography>
              </Box>
            </Box>

            {/* Título */}
            <Typography
              variant="h4"
              component="h1"
              sx={{
                color: "primary.main",
                fontWeight: "bold",
                mb: 3,
                fontSize: { xs: "1.75rem", md: "2.125rem" },
                lineHeight: 1.3,
              }}
            >
              {noticia.title}
            </Typography>

            {/* Conteúdo completo */}
            <Box sx={{ mt: 4 }}>{formatContent(noticia.content)}</Box>

            {/* Seção PIX para Assistência Social e Dízimos */}
            {(noticia.id === 3 || noticia.id === 5) && (
              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  backgroundColor: "#f8f9fa",
                  borderRadius: 2,
                  textAlign: "center",
                  border: "2px dashed #e0e0e0",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: "primary.main",
                    fontWeight: "bold",
                    mb: 2,
                  }}
                >
                  💳 {noticia.id === 3 ? "Faça sua doação via PIX" : "Contribua com Dízimos e Ofertas via PIX"}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    color: "#666",
                  }}
                >
                  {noticia.id === 3 
                    ? "Escaneie o QR Code abaixo para fazer sua contribuição:" 
                    : "Escaneie o QR Code abaixo para contribuir com dízimos e ofertas:"
                  }
                </Typography>
                <Box
                  component="img"
                  src={noticia.id === 3 ? "/images/chave_pix_assistencia.jpeg" : "/images/chave_pix_dizimo.jpeg"}
                  alt={noticia.id === 3 ? "QR Code PIX Assistência Social" : "QR Code PIX Dízimos e Ofertas"}
                  sx={{
                    width: { xs: "250px", md: "300px" },
                    height: "auto",
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    backgroundColor: "white",
                    p: 1,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    mt: 2,
                    color: "#666",
                    fontStyle: "italic",
                  }}
                >
                  Chave PIX: {noticia.pixKey}
                </Typography>
              </Box>
            )}

            {/* Botão de ação final */}
            <Box sx={{ textAlign: "center", mt: 5 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<ArrowBack />}
                onClick={() => navigate("/home")}
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  fontWeight: "bold",
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                }}
              >
                Ver Mais Notícias
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
